/**
 * Printer Management Zustand store — 2026-07-21 overhaul.
 *
 * Status now comes primarily from the backend's cached bulk sweep
 * (merged into each printer as `printer.status` by the API); the
 * `liveStatus` map only holds newer per-printer "Check now" results
 * layered on top. Deployment is the cloud-native single-POST pipeline
 * (deploy/update -> poll /status/:id) replacing the legacy 4-step flow.
 */

import { create } from 'zustand';
import {
  fetchPrinterApps,
  fetchBusinessStructure,
  fetchPrinterDrivers,
  fetchSecurityGroups,
  fetchBusinessOfficeCode,
  fetchPrinterCheckStatus,
  fetchPrinterSnmpStatus,
  fetchDeploymentStatus,
  fetchDriverLibrary,
  fetchRecentDeployments,
  runStatusSweep,
  checkExistingPrinterApp,
  deployPrinter,
  updatePrinterDeployment,
  resolveBusinessFromCodes,
  fetchPrinterApp,
} from '@/api/endpoints/printers.js';

/** Extract the printer's IP from its Intune description (metadata store). */
export function extractIP(description) {
  if (!description) return null;
  const m = description.match(/\b(?:IP[:\s]*)?(\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3})\b/i);
  return m ? m[1] : null;
}

export function extractWebPort(description) {
  const m = String(description || '').match(/Web Port:\s*(\d+)/i);
  return m ? parseInt(m[1], 10) : 80;
}

export function extractSerial(description) {
  const m = String(description || '').match(/Serial:\s*([^|]+)/i);
  return m ? m[1].trim() : null;
}

/**
 * Keep the description's `IP:` / `Web Port:` tokens in sync with the form
 * fields (beta behaviour) while preserving every other token (Serial,
 * KPrint, free text). Segments are pipe-delimited like the backend's
 * mergedPrinterDescription.
 */
export function syncDescriptionTokens(description, ip, webPort) {
  const segments = String(description || '')
    .split('|')
    .map((s) => s.trim())
    .filter(Boolean);
  let hasIp = false;
  let hasPort = false;
  const next = segments.map((segment) => {
    if (/^IP\s*:/i.test(segment)) { hasIp = true; return `IP: ${ip}`; }
    if (/^Web\s*Port\s*:/i.test(segment)) { hasPort = true; return `Web Port: ${webPort}`; }
    return segment;
  });
  if (!hasIp && ip) next.unshift(`IP: ${ip}`);
  if (!hasPort && webPort) {
    const ipIdx = next.findIndex((s) => /^IP\s*:/i.test(s));
    next.splice(ipIdx === -1 ? next.length : ipIdx + 1, 0, `Web Port: ${webPort}`);
  }
  return next.join(' | ');
}

// KPrint config lives as tokens in the Intune description. Returns null when
// the printer has no KPrint setup (opens the modal in edit mode).
export function extractKPrintData(description) {
  if (!description) return null;
  const output = description.match(/KPrint Output:\s*([^|]+)/i)?.[1]?.trim();
  const hostedIP = description.match(/KPrint IP:\s*([^|]+)/i)?.[1]?.trim();
  const spools = description.match(/KPrint Spools:\s*([^|]+)/i)?.[1]
    ?.split(',').map((s) => s.trim()).filter(Boolean);
  return (output || hostedIP || spools?.length) ? { output: output || '', hostedIP: hostedIP || '', spools: spools || [] } : null;
}

const WIZARD_INITIAL = {
  open: false,
  mode: 'create',            // 'create' | 'edit'
  appId: null,               // edit mode: the app being superseded
  step: 0,                   // 0 Site, 1 Printer, 2 Driver, 3 Groups, 4 Review & Deploy
  business: '',
  office: '',
  namePrefix: '',            // locked '{BizCode}-{SiteCode}-' segment
  printerName: '',           // user-typed suffix (create) / full name (edit)
  printerIP: '',
  webPort: 80,
  driverId: '',
  driverModelName: '',       // multi-model packages: exact .inf model to install
  description: '',
  ipCheck: null,             // { online, responseTime } from the live reachability probe
  ipChecking: false,
  existingApp: null,         // supersedence prompt result on Review step
  checkingExisting: false,
};

const DEPLOY_PROGRESS_INITIAL = {
  running: false,
  deploymentId: null,
  pct: 0,
  stepText: '',
  log: [],
  error: null,
  done: false,
};

const findPrinter = (list, appId) => {
  const id = String(appId || '').toLowerCase();
  return (Array.isArray(list) ? list : []).find((p) => String(p?.id || '').toLowerCase() === id) || null;
};

export const usePrintersStore = create((set, get) => ({
  // ── Printer list (status pre-merged server-side from the sweep) ─────────
  allPrinters: [],
  loadingPrinters: false,
  printersError: null,
  statusSweptAt: null,
  fromCache: false,
  searchQuery: '',
  pendingSearchFilter: null,
  pendingOpenAppId: null,     // set by another tab (Apps) to open one printer once the list is in
  sweeping: false,

  // Per-printer live check results, layered over printer.status.
  liveStatus: {},            // printerId -> snmp/tcp result (+ checking flag)

  // ── Sub-tab ──────────────────────────────────────────────────────────────
  subTab: 'printers',        // printers | drivers | deployments
  setSubTab(key) { set({ subTab: key }); },

  // ── Driver Library ───────────────────────────────────────────────────────
  drivers: [],
  driversConfigured: false,
  loadingDrivers: false,

  // ── Deployments history ──────────────────────────────────────────────────
  deployments: [],
  loadingDeployments: false,

  // ── Reference data ───────────────────────────────────────────────────────
  businessStructure: null,
  loadingBusiness: false,

  // ── Modals ───────────────────────────────────────────────────────────────
  detailPrinter: null,       // printer object -> PrinterDetailModal open
  wizard: { ...WIZARD_INITIAL },
  deployProgress: { ...DEPLOY_PROGRESS_INITIAL },
  assignmentRules: { required: [], available: [], uninstall: [] },
  assignmentGroupsModalOpen: false,
  kprintModal: { open: false, appId: null, viewMode: false, output: '', hostedIP: '', spools: [''] },

  // ── Printer list actions ─────────────────────────────────────────────────

  async loadPrinters(forceRefresh = false) {
    if (get().loadingPrinters && !forceRefresh) return;
    set({ loadingPrinters: true, printersError: null });
    try {
      // forceRefresh bypasses the backend's 2h Intune cache (?refresh=true) —
      // without it, deletes/deploys kept showing the stale list for hours.
      const data = await fetchPrinterApps(forceRefresh);
      const raw = data?.applications ?? data?.printers ?? data;
      const list = Array.isArray(raw) ? raw : [];
      const { pendingSearchFilter, pendingOpenAppId } = get();
      const opened = pendingOpenAppId ? findPrinter(list, pendingOpenAppId) : null;
      set({
        pendingOpenAppId: null,
        ...(opened ? { detailPrinter: opened, subTab: 'printers' } : {}),
        allPrinters: list,
        loadingPrinters: false,
        statusSweptAt: data?.statusSweptAt ?? null,
        fromCache: Boolean(data?.fromCache),
        pendingSearchFilter: null,
        ...(pendingSearchFilter && !opened ? { searchQuery: pendingSearchFilter } : {}),
      });
    } catch (err) {
      set({
        allPrinters: [],
        loadingPrinters: false,
        printersError: err instanceof Error ? err.message : 'Failed to load printers',
      });
    }
  },

  setSearchQuery(q) { set({ searchQuery: q }); },
  setPendingSearchFilter(name) { set({ pendingSearchFilter: name }); },

  /**
   * One click from the Apps tab: open this printer's detail (or, for a
   * "DRV >" driver app, the Drivers sub-tab). Works whether or not this tab
   * has loaded yet: an already-loaded list opens at once; otherwise the next
   * list read opens it, falling back to searching for the printer's name.
   */
  openFromApps(appId, appName = '') {
    const name = String(appName || '');
    if (/^\s*DRV\b/i.test(name)) { set({ subTab: 'drivers' }); return; }
    const found = findPrinter(get().allPrinters, appId);
    if (found) { set({ subTab: 'printers', detailPrinter: found }); return; }
    set({ subTab: 'printers', pendingOpenAppId: appId, pendingSearchFilter: name.replace(/^\s*PTR\s*>?\s*/i, '').trim() || null });
    get().loadPrinters();
  },

  /** Client-side filtered view (name/publisher/description/serial/IP/site). */
  getFilteredPrinters() {
    const { allPrinters, searchQuery } = get();
    const q = (searchQuery || '').toLowerCase().trim();
    if (!q) return allPrinters;
    return allPrinters.filter((p) => {
      const status = get().statusFor(p);
      return [p.name, p.publisher, p.description, p.businessCode, p.officeCode, status?.serialNumber, status?.model, extractIP(p.description)]
        .some((v) => String(v || '').toLowerCase().includes(q));
    });
  },

  /** Effective status: live check result (newer) over the sweep entry. */
  statusFor(printer) {
    if (!printer) return null;
    const live = get().liveStatus[printer.id];
    const swept = printer.status || null;
    if (live && swept) return { ...swept, ...live };
    return live || swept;
  },

  /** Full live SNMP check for one printer ("Check now"). */
  async checkPrinterNow(printer) {
    const ip = printer.ip || extractIP(printer.description);
    if (!ip) return null;
    set((s) => ({ liveStatus: { ...s.liveStatus, [printer.id]: { ...(s.liveStatus[printer.id] || {}), checking: true } } }));
    try {
      const data = await fetchPrinterSnmpStatus(ip);
      set((s) => ({
        liveStatus: {
          ...s.liveStatus,
          [printer.id]: { ...data, checking: false, checkedAt: new Date().toISOString() },
        },
      }));
      return data;
    } catch {
      set((s) => ({
        liveStatus: { ...s.liveStatus, [printer.id]: { ...(s.liveStatus[printer.id] || {}), checking: false, error: true } },
      }));
      return null;
    }
  },

  /** Manual "Refresh All": one connector bulk sweep, then reload the list. */
  async runSweepNow() {
    if (get().sweeping) return;
    set({ sweeping: true });
    try {
      await runStatusSweep();
      await get().loadPrinters(true);
      set({ sweeping: false, liveStatus: {} });
      return true;
    } catch (err) {
      set({ sweeping: false });
      throw err;
    }
  },

  // ── Driver Library ───────────────────────────────────────────────────────

  async loadDriverLibrary() {
    if (get().loadingDrivers) return;
    set({ loadingDrivers: true });
    try {
      const data = await fetchDriverLibrary();
      set({ drivers: Array.isArray(data?.drivers) ? data.drivers : [], driversConfigured: Boolean(data?.configured), loadingDrivers: false });
    } catch {
      set({ loadingDrivers: false });
    }
  },

  // ── Deployments history ──────────────────────────────────────────────────

  async loadDeployments() {
    set({ loadingDeployments: true });
    try {
      const data = await fetchRecentDeployments();
      set({ deployments: Array.isArray(data?.deployments) ? data.deployments : [], loadingDeployments: false });
    } catch {
      set({ loadingDeployments: false });
    }
  },

  // ── Reference data ───────────────────────────────────────────────────────

  async loadBusinessStructure() {
    if (get().businessStructure || get().loadingBusiness) return;
    set({ loadingBusiness: true });
    try {
      const data = await fetchBusinessStructure();
      set({ businessStructure: data, loadingBusiness: false });
    } catch {
      set({ loadingBusiness: false });
    }
  },

  getBusinessNames() {
    const bs = get().businessStructure?.businessStructure;
    if (!bs || typeof bs !== 'object') return [];
    return Object.keys(bs).sort((a, b) => a.localeCompare(b));
  },

  getOfficesForBusiness(businessName) {
    const bs = get().businessStructure?.businessStructure;
    const offices = bs?.[businessName];
    if (!Array.isArray(offices)) return [];
    return offices.filter((o) => typeof o === 'string');
  },

  // ── Detail modal ─────────────────────────────────────────────────────────

  openDetail(printer) { set({ detailPrinter: printer }); },
  closeDetail() { set({ detailPrinter: null }); },

  // ── Deploy wizard ────────────────────────────────────────────────────────

  openWizard() {
    set({
      wizard: { ...WIZARD_INITIAL, open: true },
      deployProgress: { ...DEPLOY_PROGRESS_INITIAL },
      assignmentRules: { required: [], available: [], uninstall: [] },
    });
  },

  /** Edit = the same wizard prefilled; deploys via the supersedence path. */
  openWizardForEdit(printer) {
    const status = get().statusFor(printer);
    let description = printer.description === 'No description available' ? '' : (printer.description || '');
    // Carry the serial into the new description (legacy edit behaviour).
    const serial = status?.serialNumber || extractSerial(description);
    if (serial && !/Serial\s*:/i.test(description)) {
      description = description ? `${description} | Serial: ${serial}` : `Serial: ${serial}`;
    }
    // Prefill the driver from the make match — always the LATEST library
    // version (deploys package versions[0]).
    const make = String(printer.publisher || '').toLowerCase();
    const matchedDriver = make && make !== 'unknown publisher'
      ? get().drivers.find((d) => !d.awaitingUpload && (d.make.toLowerCase() === make || make.includes(d.make.toLowerCase())))
      : null;
    set({
      wizard: {
        ...WIZARD_INITIAL,
        open: true,
        mode: 'edit',
        appId: printer.id,
        step: 0,
        printerName: (printer.name || '').replace(/^PTR\s*>\s*/i, ''),
        printerIP: printer.ip || extractIP(printer.description) || '',
        webPort: extractWebPort(printer.description),
        driverId: matchedDriver?.id || '',
        description,
      },
      deployProgress: { ...DEPLOY_PROGRESS_INITIAL },
      assignmentRules: { required: [], available: [], uninstall: [] },
    });
    // Resolve the printer name's {BIZ}-{SITE} codes back to display names.
    if (printer.businessCode) {
      void resolveBusinessFromCodes(printer.businessCode, printer.officeCode || '')
        .then((resolved) => {
          const wizard = get().wizard;
          if (!wizard.open || wizard.appId !== printer.id) return; // stale
          set((s) => ({
            wizard: {
              ...s.wizard,
              business: resolved?.business || s.wizard.business,
              office: resolved?.office || s.wizard.office,
            },
          }));
        })
        .catch(() => {});
    }
    // Pull the app's CURRENT group assignments into the Groups step so the
    // redeploy shows (and can edit) what's really assigned. Exclusion rows
    // are derived server-side from required/available, so they're skipped.
    void fetchPrinterApp(printer.id)
      .then((detail) => {
        const wizard = get().wizard;
        if (!wizard.open || wizard.appId !== printer.id) return; // stale
        const rules = { required: [], available: [], uninstall: [] };
        for (const a of detail?.assignments || []) {
          if (a.targetType?.includes('exclusion')) continue;
          if (rules[a.intent] && a.groupId && a.groupId !== 'N/A') {
            rules[a.intent].push({ id: a.groupId, name: a.groupName });
          }
        }
        set({ assignmentRules: rules });
      })
      .catch(() => {});
  },

  closeWizard() {
    set((s) => ({ wizard: { ...s.wizard, open: false } }));
  },

  patchWizard(patch) {
    set((s) => ({ wizard: { ...s.wizard, ...patch } }));
  },

  setWizardStep(step) {
    set((s) => ({ wizard: { ...s.wizard, step } }));
  },

  /** Office picked: resolve the locked name prefix + auto assignment rules. */
  async onWizardOfficeSelected(business, office) {
    if (!business || !office) return;
    try {
      const [codeData, groupsData] = await Promise.allSettled([
        fetchBusinessOfficeCode(business, office),
        fetchSecurityGroups(business, office),
      ]);
      if (codeData.status === 'fulfilled' && codeData.value?.code) {
        set((s) => ({ wizard: { ...s.wizard, namePrefix: `${codeData.value.code}-` } }));
      }
      if (groupsData.status === 'fulfilled') {
        const groups = (list) => (Array.isArray(list) ? list.filter((g) => g && g.id) : []);
        set({
          assignmentRules: {
            required: groups(groupsData.value?.required),
            available: groups(groupsData.value?.available),
            uninstall: groups(groupsData.value?.uninstall),
          },
        });
      }
    } catch { /* prefix/rules stay empty — validation blocks Next */ }
  },

  /** Live reachability probe on the Printer step (IP blur). */
  async checkWizardIp() {
    const ip = get().wizard.printerIP.trim();
    if (!/^(?:\d{1,3}\.){3}\d{1,3}$/.test(ip)) {
      set((s) => ({ wizard: { ...s.wizard, ipCheck: null } }));
      return;
    }
    set((s) => ({ wizard: { ...s.wizard, ipChecking: true } }));
    try {
      const data = await fetchPrinterCheckStatus(ip);
      set((s) => ({ wizard: { ...s.wizard, ipChecking: false, ipCheck: { online: Boolean(data?.online), responseTime: data?.responseTime ?? null } } }));
    } catch {
      set((s) => ({ wizard: { ...s.wizard, ipChecking: false, ipCheck: { online: false, responseTime: null } } }));
    }
  },

  /** Review step: supersedence prompt when an app already exists. */
  async checkWizardExisting() {
    const { wizard } = get();
    if (wizard.mode === 'edit') return; // edit is always a supersedence update
    const fullName = `${wizard.namePrefix || ''}${wizard.printerName.trim()}`;
    if (!fullName) return;
    set((s) => ({ wizard: { ...s.wizard, checkingExisting: true } }));
    try {
      const data = await checkExistingPrinterApp(fullName);
      set((s) => ({ wizard: { ...s.wizard, checkingExisting: false, existingApp: data?.existingApp ?? null } }));
    } catch {
      set((s) => ({ wizard: { ...s.wizard, checkingExisting: false, existingApp: null } }));
    }
  },

  setAssignmentRules(rules) { set({ assignmentRules: rules }); },
  openAssignmentGroupsModal() { set({ assignmentGroupsModalOpen: true }); },
  closeAssignmentGroupsModal() { set({ assignmentGroupsModalOpen: false }); },

  // ── Deploy / update run ─────────────────────────────────────────────────

  _setProgress(patch) {
    set((s) => ({ deployProgress: { ...s.deployProgress, ...patch } }));
  },

  _appendLog(line) {
    set((s) => ({ deployProgress: { ...s.deployProgress, log: [...s.deployProgress.log, { text: line, ts: Date.now() }] } }));
  },

  /**
   * Kick off the deployment (create: fresh app or supersedence when
   * existingApp was confirmed; edit: always the update/supersedence path)
   * and poll the deployment record until it completes.
   */
  async runWizardDeployment({ superseding = false } = {}) {
    const { wizard, assignmentRules } = get();
    const fullName = wizard.mode === 'edit'
      ? wizard.printerName.trim()
      : `${wizard.namePrefix || ''}${wizard.printerName.trim()}`;

    const payload = {
      printerName: fullName,
      printerIP: wizard.printerIP.trim(),
      webPort: wizard.webPort,
      business: wizard.business,
      office: wizard.office,
      printerMake: get().drivers.find((d) => d.id === wizard.driverId)?.make || '',
      driverId: wizard.driverId,
      ...(wizard.driverModelName ? { driverModelName: wizard.driverModelName } : {}),
      description: wizard.description.trim(),
      assignmentRules,
    };

    set({ deployProgress: { ...DEPLOY_PROGRESS_INITIAL, running: true, stepText: 'Starting deployment…' } });
    get()._appendLog(wizard.mode === 'edit' || superseding ? 'Starting update deployment (supersedence)…' : 'Starting deployment…');

    try {
      let response;
      if (wizard.mode === 'edit') {
        response = await updatePrinterDeployment({ ...payload, appId: wizard.appId });
      } else if (superseding && wizard.existingApp?.id) {
        response = await updatePrinterDeployment({ ...payload, appId: wizard.existingApp.id });
      } else {
        response = await deployPrinter(payload);
      }
      const deploymentId = response?.deploymentId;
      if (!deploymentId) throw new Error('No deployment id returned.');
      get()._setProgress({ deploymentId });
      get()._appendLog(`Deployment started (${deploymentId}).`);
      await get()._pollDeployment(deploymentId);
      return true;
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      get()._setProgress({ running: false, error: msg, stepText: `Deployment failed: ${msg}` });
      get()._appendLog(`ERROR: ${msg}`);
      return false;
    }
  },

  async _pollDeployment(deploymentId) {
    let lastStepCount = 0;
    const MAX = 120; // 10 min at 5s
    for (let attempt = 0; attempt < MAX; attempt++) {
      await new Promise((r) => setTimeout(r, 5000));
      const status = await fetchDeploymentStatus(deploymentId);
      const steps = Array.isArray(status?.steps) ? status.steps : [];
      for (const step of steps.slice(lastStepCount)) {
        get()._appendLog(step.error ? `${step.step}: ${step.error}` : step.message ? `${step.step} — ${step.message}` : step.step);
      }
      lastStepCount = steps.length;
      get()._setProgress({ pct: status?.progress ?? 0, stepText: steps[steps.length - 1]?.step || 'Running…' });

      if (status?.status === 'completed') {
        get()._setProgress({ pct: 100, running: false, done: true, stepText: 'Deployment complete' });
        get().loadPrinters(true);
        return;
      }
      if (status?.status === 'failed') {
        throw new Error(steps[steps.length - 1]?.error || 'Deployment failed');
      }
    }
    throw new Error('Deployment timed out after 10 minutes');
  },

  // ── KPrint modal ─────────────────────────────────────────────────────────

  openKPrintModal(appId, existing = null) {
    set({
      kprintModal: {
        open: true,
        appId,
        viewMode: !!existing,
        output: existing?.output ?? '',
        hostedIP: existing?.hostedIP ?? '',
        spools: existing?.spools?.length ? existing.spools : [''],
      },
    });
  },

  closeKPrintModal() { set((s) => ({ kprintModal: { ...s.kprintModal, open: false } })); },
  patchKPrintModal(patch) { set((s) => ({ kprintModal: { ...s.kprintModal, ...patch } })); },
  enableKPrintEdit() { set((s) => ({ kprintModal: { ...s.kprintModal, viewMode: false } })); },
  addKPrintSpool() { set((s) => ({ kprintModal: { ...s.kprintModal, spools: [...s.kprintModal.spools, ''] } })); },
  updateKPrintSpool(idx, value) {
    set((s) => {
      const spools = [...s.kprintModal.spools];
      spools[idx] = value;
      return { kprintModal: { ...s.kprintModal, spools } };
    });
  },
  removeKPrintSpool(idx) {
    set((s) => {
      const spools = s.kprintModal.spools.filter((_, i) => i !== idx);
      return { kprintModal: { ...s.kprintModal, spools: spools.length ? spools : [''] } };
    });
  },
}));
